import { useCallback } from "react";

import type { Invoice } from "~/shared/schemas/invoice-schema.ts";

import type { PaymentExportColumn } from "../utils/get-payment-columns.tsx";

const CURRENCY_FIELDS = new Set(["balanceAmt", "paidAmt", "invoiceAmt"]);

const formatCsvValue = (value: unknown, field: string): string => {
	if (value == null) return "";
	if (CURRENCY_FIELDS.has(field)) return (value as number).toFixed(2);
	if (
		typeof value === "string" ||
		typeof value === "number" ||
		typeof value === "boolean"
	) {
		return String(value);
	}
	return "";
};

export const useCSVExport = () => {
	const exportToCSV = useCallback(
		(invoices: Invoice[], columns: PaymentExportColumn[]) => {
			if (invoices.length === 0) {
				return;
			}

			const headers = columns.map((col) => col.title);

			const csvRows = [
				headers.join(","),
				...invoices.map((invoice) =>
					columns
						.map((col) => {
							const value = invoice[col.field as keyof Invoice];
							const formatted = formatCsvValue(value, col.field);
							return `"${formatted.replaceAll('"', '""')}"`;
						})
						.join(","),
				),
			];

			const csvContent = csvRows.join("\n");

			const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
			const link = document.createElement("a");
			const url = URL.createObjectURL(blob);
			link.setAttribute("href", url);
			link.setAttribute(
				"download",
				`selected_invoices_${new Date().toISOString().split("T")[0]}.csv`,
			);
			link.style.visibility = "hidden";
			document.body.append(link);
			link.click();
			link.remove();
			URL.revokeObjectURL(url);
		},
		[],
	);

	return { exportToCSV };
};
